import { useEffect, useState } from 'react';

/*
 * Returns whether the app can actually reach the VPS. It pings a tiny
 * same-origin file every 15s (not just navigator.onLine), because the shop's
 * phone often stays connected to the router while the router's internet is down
 * — the exact case the seller must see. /asset-manifest.json is used because the
 * service worker never caches it, so the fetch always hits the network.
 */
const PING_URL = '/asset-manifest.json';
const PING_MS = 15000;
const PING_TIMEOUT_MS = 6000;

export default function useServerOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    let alive = true;
    const ping = async () => {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
      try {
        const res = await fetch(`${PING_URL}?t=${Date.now()}`, { cache: 'no-store', signal: ctrl.signal });
        if (alive) setOnline(res.ok);
      } catch {
        if (alive) setOnline(false);
      } finally {
        clearTimeout(timer);
      }
    };

    const onOnline = () => { setOnline(true); ping(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    ping();
    const interval = setInterval(ping, PING_MS);
    return () => {
      alive = false;
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return online;
}
